import type { Hint, Vec3 } from '../fsm/machine';

// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
// COME STA TENUTA LA FOTOCAMERA, dalla gravità nel sistema della fotocamera (x destra, y giù, z avanti: capture/imu.ts).
// Con la fotocamera orizzontale la gravità è tutta su +y e z ≈ 0; inclinandola verso il basso di θ la gravità prende una
// componente z = g·sin θ. Misurato sulla EDA61K il 13/10/2026: fotocamera inclinata di ~33° in giù, gravità (0,21; 8,31; 5,36),
// da cui asin(5,36/9,9) = 32,8°.
//
// Perché serve: una foto scattata molto dall'alto (il telefono tenuto a mezzo busto verso il pallet, ~40° di inclinazione) ha
// i verticali che convergono forte e la faccia frontale molto scorciata; nella prova a secco del 20/10/2026 erano i casi con
// i residui di posa peggiori. Le soglie sono DATI e sono dei SEGNAPOSTO: si tarano sui dati della campagna, dove inclinazione e
// rollio di ogni scatto sono registrati.
// ─────────────────────────────────────────────────────────────────────────────────────────────────────────────

export interface Orientation {
  /** Quanto la fotocamera guarda verso il basso, in gradi (negativo = verso l'alto). */
  pitchDownDeg: number;
  /** Quanto è inclinata di lato rispetto alla verticale più vicina (pistola in verticale o in orizzontale), in gradi, in [−45, 45). */
  rollDeg: number;
}

export interface OrientationLimits {
  maxPitchDownDeg: number;
  maxPitchUpDeg: number;
  maxRollDeg: number;
}

/** Segnaposto da tarare con la campagna (ADR-029: le soglie sono dati). */
export const DEFAULT_ORIENTATION_LIMITS: OrientationLimits = { maxPitchDownDeg: 30, maxPitchUpDeg: 15, maxRollDeg: 8 };

const DEG = 180 / Math.PI;

/** Inclinazione e rollio dalla gravità; null senza sensore o con un vettore nullo. */
export function orientationFromGravity(g: Vec3 | null): Orientation | null {
  if (!g) return null;
  const norm = Math.hypot(g.x, g.y, g.z);
  if (!(norm > 1e-6)) return null;
  const pitchDownDeg = Math.asin(Math.max(-1, Math.min(1, g.z / norm))) * DEG;
  // Con la fotocamera quasi a piombo la gravità sul piano immagine è piccola e il rollio non ha senso: si dice zero (l'inclinazione protesta già).
  const inPlane = Math.hypot(g.x, g.y);
  if (inPlane < 0.05 * norm) return { pitchDownDeg, rollDeg: 0 };
  const roll = Math.atan2(g.x, g.y) * DEG;
  return { pitchDownDeg, rollDeg: roll - 90 * Math.round(roll / 90) };
}

/** Il richiamo per l'operatore, o null se la posizione va bene. Guida, non blocca. */
export function evaluateOrientation(o: Orientation | null, limits: OrientationLimits): Hint | null {
  if (!o) return null;
  if (o.pitchDownDeg > limits.maxPitchDownDeg) return { code: 'orientation-too-high', hint: 'Troppo dall’alto: allontanati e punta più dritto' };
  if (o.pitchDownDeg < -limits.maxPitchUpDeg) return { code: 'orientation-too-low', hint: 'Troppo dal basso: punta la fotocamera più in giù' };
  if (Math.abs(o.rollDeg) > limits.maxRollDeg) return { code: 'orientation-tilted', hint: 'Fotocamera inclinata di lato: raddrizzala' };
  return null;
}
